import React, { useState, useMemo } from "react";
import usePagination from "src/customHooks/usePagination";
import Pagination from "src/components/pagination";
import { Link, Navigate, useNavigate } from "react-router-dom";
import PageLoading from "src/components/pageLoading";
import PageHeader from "src/components/pageHeader";
import { useCountRewardsQuery, useGetRewardsQuery } from "../rewards.service";
import RewardCard from "../components/RewardCard";
import { Reward } from "../types/reward.entity";
import { useCountUnseenKarmaExchangeRequestedCountriesQuery } from "src/features/karma-exchanges/karma-exchanges.service";
import {
  FaPlus,
  FaSearch,
  FaFilter,
  FaBoxOpen,
  FaRocket,
  FaGift,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";

const RewardsPage = () => {
  const [filterCategory, setFilterCategory] = useState<string>("ALL");
  const [filterStatus, setFilterStatus] = useState<string>("ALL");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { data: unseenCountryRequestsCount = 0 } = useCountUnseenKarmaExchangeRequestedCountriesQuery();

  const {
    data,
    numberOfPages,
    currentPage,
    next,
    prev,
    isSuccess,
    isError,
    isLoading,
    isFetching,
  } = usePagination<Reward>({
    useGetDataQuery: useGetRewardsQuery,
    useCountData: useCountRewardsQuery,
  });

  const displayedRewards = useMemo(() => {
    let filtered = data || [];

    // Category filter
    if (filterCategory !== "ALL") {
      filtered = filtered.filter((r) => r.category === filterCategory);
    }

    // Status filter
    if (filterStatus === "AVAILABLE") {
      filtered = filtered.filter((r) => r.isAvailable);
    } else if (filterStatus === "UNAVAILABLE") {
      filtered = filtered.filter((r) => !r.isAvailable);
    }

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.description.toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [data, filterCategory, filterStatus, searchQuery]);

  // Stats
  const totalRewards = data?.length || 0;
  const activeRewards = data?.filter((r) => r.isAvailable).length || 0;
  const inactiveRewards = totalRewards - activeRewards;

  const navigate = useNavigate();

  const handleEdit = (reward: Reward) => {
    navigate(`/rewards/${reward.id}/edit`);
  };

  return isSuccess ? (
    <div className="page_container space-y-4">
      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <PageHeader text="Rewards" />

        <div className="flex items-center gap-3 flex-wrap">
          <Link
            to="/rewards/requested-countries"
            className="relative inline-flex items-center gap-2 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg font-medium text-sm transition-colors no-underline"
          >
            <FaBoxOpen className="w-3.5 h-3.5" />
            Country Requests
            {unseenCountryRequestsCount > 0 && (
              <span className="inline-flex min-w-[20px] h-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white">
                {unseenCountryRequestsCount > 99 ? "99+" : unseenCountryRequestsCount}
              </span>
            )}
          </Link>

          <button
            onClick={() => navigate("/rewards/create")}
            className="inline-flex items-center gap-2 bg-gray-900 hover:bg-brand-500 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors cursor-pointer"
          >
            <FaPlus className="w-3.5 h-3.5" />
            Add Reward
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl px-5 py-4">
          <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center text-brand-500">
            <FaGift className="w-4.5 h-4.5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 tabular-nums leading-none">
              {totalRewards}
            </p>
            <p className="text-xs text-gray-500 font-medium mt-1">Total Rewards</p>
          </div>
        </div>

        <div className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl px-5 py-4">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-500">
            <FaCheckCircle className="w-4.5 h-4.5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 tabular-nums leading-none">
              {activeRewards}
            </p>
            <p className="text-xs text-gray-500 font-medium mt-1">Active</p>
          </div>
        </div>

        <div className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl px-5 py-4">
          <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center text-red-500">
            <FaTimesCircle className="w-4.5 h-4.5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 tabular-nums leading-none">
              {inactiveRewards}
            </p>
            <p className="text-xs text-gray-500 font-medium mt-1">Inactive</p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 p-4 bg-white rounded-xl border border-gray-200">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Search rewards…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white focus:border-brand-300 focus:ring-2 focus:ring-brand-100 transition-all outline-none"
          />
        </div>

        {/* Filter controls */}
        <div className="flex items-center gap-2 flex-wrap">
          <FaFilter className="w-3 h-3 text-gray-400" />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:border-brand-300 focus:ring-2 focus:ring-brand-100 transition-all outline-none cursor-pointer"
          >
            <option value="ALL">All Categories</option>
            <option value="DIGITAL">Digital</option>
            <option value="PHISICAL">Physical</option>
            <option value="VOUCHER">Voucher</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:border-brand-300 focus:ring-2 focus:ring-brand-100 transition-all outline-none cursor-pointer"
          >
            <option value="ALL">All Statuses</option>
            <option value="AVAILABLE">Available</option>
            <option value="UNAVAILABLE">Unavailable</option>
          </select>
        </div>
      </div>

      {/* Active filter pills */}
      {(filterCategory !== "ALL" ||
        filterStatus !== "ALL" ||
        searchQuery.trim()) && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-400 font-medium">
              Active filters:
            </span>
            {filterCategory !== "ALL" && (
              <button
                onClick={() => setFilterCategory("ALL")}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-50 text-brand-600 text-xs font-medium hover:bg-brand-100 transition-colors cursor-pointer"
              >
                {filterCategory}
                <span className="ml-0.5">×</span>
              </button>
            )}
            {filterStatus !== "ALL" && (
              <button
                onClick={() => setFilterStatus("ALL")}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-50 text-brand-600 text-xs font-medium hover:bg-brand-100 transition-colors cursor-pointer"
              >
                {filterStatus}
                <span className="ml-0.5">×</span>
              </button>
            )}
            {searchQuery.trim() && (
              <button
                onClick={() => setSearchQuery("")}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-50 text-brand-600 text-xs font-medium hover:bg-brand-100 transition-colors cursor-pointer"
              >
                "{searchQuery}"
                <span className="ml-0.5">×</span>
              </button>
            )}
          </div>
        )}

      {/* Content area */}
      {isLoading || isFetching ? (
        /* Skeleton Loader */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col animate-pulse"
            >
              <div className="bg-gray-100 h-44 w-full" />
              <div className="p-4 flex-1 space-y-3">
                <div className="h-3 bg-gray-100 rounded w-20" />
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="pt-3 border-t border-gray-100 flex justify-between items-center">
                  <div className="h-5 bg-gray-100 rounded w-20" />
                  <div className="h-7 w-7 bg-gray-100 rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : displayedRewards.length > 0 ? (
        /* Rewards Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {displayedRewards.map((reward) => (
            <RewardCard key={reward.id} reward={reward} onEdit={handleEdit} />
          ))}
        </div>
      ) : (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-16 px-8">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
            <FaBoxOpen className="w-7 h-7 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">
            No Rewards Found
          </h3>
          <p className="text-gray-500 max-w-sm text-center text-sm mb-5">
            {searchQuery || filterCategory !== "ALL" || filterStatus !== "ALL"
              ? "No rewards match your current filters. Try adjusting your search criteria."
              : "Your reward catalog is empty. Create your first reward to get started."}
          </p>
          {!searchQuery &&
            filterCategory === "ALL" &&
            filterStatus === "ALL" && (
              <button
                onClick={() => navigate("/rewards/create")}
                className="inline-flex items-center gap-2 bg-gray-900 hover:bg-brand-500 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors cursor-pointer"
              >
                <FaRocket className="w-3.5 h-3.5" />
                Create First Reward
              </button>
            )}
        </div>
      )}

      {/* Pagination */}
      {displayedRewards.length > 0 && (
        <Pagination
          numberOfPages={numberOfPages}
          currentPage={currentPage}
          prev={prev}
          next={next}
          isLoading={isLoading || isFetching}
        />
      )}
    </div>
  ) : isError ? (
    <Navigate to="/505" />
  ) : (
    <PageLoading />
  );
};

export default RewardsPage;
